import React, { useState, useEffect } from 'react';
import QRCode from 'react-qr-code';
import { FaShieldAlt, FaCopy, FaCheck, FaEye, FaEyeSlash } from 'react-icons/fa';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface TwoFactorSetupProps {
  onComplete: () => void;
  onCancel: () => void;
}

interface SetupData {
  qrCodeUrl: string;
  backupCodes: string[];
  manualEntryKey: string;
}

export const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState<'setup' | 'verify' | 'backup'>('setup');
  const [setupData, setSetupData] = useState<SetupData | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState<boolean[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  useEffect(() => {
    if (step === 'setup') {
      initializeSetup();
    }
  }, [step]);

  const initializeSetup = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/2fa/setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to setup 2FA');
      }

      const data = await response.json();
      setSetupData(data);
      setCopiedCodes(new Array(data.backupCodes.length).fill(false));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/2fa/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('riskninja-token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token: verificationCode })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Verification failed');
      }

      setStep('backup');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, index?: number) => {
    try {
      await navigator.clipboard.writeText(text);
      
      if (index !== undefined) {
        const newCopiedCodes = [...copiedCodes];
        newCopiedCodes[index] = true;
        setCopiedCodes(newCopiedCodes);
        setTimeout(() => {
          const resetCopiedCodes = [...newCopiedCodes];
          resetCopiedCodes[index] = false;
          setCopiedCodes(resetCopiedCodes);
        }, 2000);
      } else {
        setCopiedKey(true);
        setTimeout(() => setCopiedKey(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const copyAllBackupCodes = async () => {
    if (!setupData) return;
    
    const codesText = setupData.backupCodes.join('\n');
    await copyToClipboard(codesText);
  };

  if (loading && !setupData) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto">
        <div className="flex items-center justify-center mb-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
        <p className="text-center text-gray-600">Setting up two-factor authentication...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center mb-6">
        <FaShieldAlt className="text-green-600 text-2xl mr-3" />
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Two-Factor Authentication Setup
          </h2>
          <p className="text-sm text-gray-600">
            Add an extra layer of security to your account
          </p>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center mb-8">
        <div className={`flex items-center ${step === 'setup' ? 'text-blue-600' : 'text-green-600'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            step === 'setup' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
          }`}>
            1
          </div>
          <span className="ml-2 text-sm font-medium">Setup</span>
        </div>
        <div className="flex-1 h-px bg-gray-300 mx-4"></div>
        <div className={`flex items-center ${
          step === 'verify' ? 'text-blue-600' : 
          step === 'backup' ? 'text-green-600' : 'text-gray-400'
        }`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            step === 'verify' ? 'bg-blue-100 text-blue-600' : 
            step === 'backup' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
          }`}>
            2
          </div>
          <span className="ml-2 text-sm font-medium">Verify</span>
        </div>
        <div className="flex-1 h-px bg-gray-300 mx-4"></div>
        <div className={`flex items-center ${step === 'backup' ? 'text-blue-600' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
            step === 'backup' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
          }`}>
            3
          </div>
          <span className="ml-2 text-sm font-medium">Backup Codes</span>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Setup Step */}
      {step === 'setup' && setupData && (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Scan QR Code with Microsoft Authenticator
            </h3>
            <div className="bg-white p-6 rounded-lg border-2 border-gray-200 inline-block shadow-sm">
              <QRCode 
                value={setupData.qrCodeUrl} 
                size={256}
                bgColor="#FFFFFF"
                fgColor="#000000"
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              />
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Manual Entry</h4>
            <p className="text-sm text-gray-600 mb-3">
              If you can't scan the QR code, manually enter this key in your authenticator app:
            </p>
            <div className="flex items-center space-x-2">
              <code className="flex-1 bg-white px-3 py-2 border rounded text-sm font-mono break-all">
                {setupData.manualEntryKey}
              </code>
              <button
                onClick={() => copyToClipboard(setupData.manualEntryKey)}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                title="Copy key"
              >
                {copiedKey ? <FaCheck className="text-green-600" /> : <FaCopy />}
              </button>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => setStep('verify')}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Verify Step */}
      {step === 'verify' && (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Enter Verification Code
            </h3>
            <p className="text-gray-600 mb-6">
              Enter the 6-digit code from your Microsoft Authenticator app
            </p>
            
            <div className="max-w-xs mx-auto">
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerificationCode(value);
                }}
                placeholder="000000"
                className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength={6}
              />
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => setStep('setup')}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={verifyAndEnable}
              disabled={loading || verificationCode.length !== 6}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : 'Verify & Enable'}
            </button>
          </div>
        </div>
      )}

      {/* Backup Codes Step */}
      {step === 'backup' && setupData && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Save Your Backup Codes
            </h3>
            <p className="text-gray-600 mb-4">
              Store these backup codes in a safe place. You can use them to access your account if you lose your phone.
            </p>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
              <p className="text-yellow-800 text-sm font-medium">
                ⚠️ Important: Each backup code can only be used once. Print or save them securely.
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">Backup Codes</h4>
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowBackupCodes(!showBackupCodes)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                  title={showBackupCodes ? 'Hide codes' : 'Show codes'}
                >
                  {showBackupCodes ? <FaEyeSlash /> : <FaEye />}
                </button>
                <button
                  onClick={copyAllBackupCodes}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                  title="Copy all codes"
                >
                  <FaCopy />
                </button>
              </div>
            </div>
            
            {showBackupCodes && (
              <div className="grid grid-cols-2 gap-2">
                {setupData.backupCodes.map((code, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <code className="flex-1 bg-white px-3 py-2 border rounded text-sm font-mono">
                      {code}
                    </code>
                    <button
                      onClick={() => copyToClipboard(code, index)}
                      className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                    >
                      {copiedCodes[index] ? <FaCheck className="text-green-600" /> : <FaCopy />}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <p className="text-green-800 text-sm">
              ✅ Two-factor authentication has been successfully enabled for your account!
            </p>
          </div>

          <button
            onClick={onComplete}
            className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Complete Setup
          </button>
        </div>
      )}
    </div>
  );
};